'use client';

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Settings, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import PopupTemplate from '@/app/ui/popupTemplate';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { languageModels, LanguageModel, ImageGenMode, getCurrentImageModelPrice } from '../../data/chatSetting/settingOptions';
import { useGlobalChatState } from '../../hooks/useGlobalChatState';

interface ChatSettingPopupProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ChatSettingPopup({ isOpen, onClose }: ChatSettingPopupProps) {
  const t = useTranslations('chat');
  const [globalChatStateData, globalActions] = useGlobalChatState();
  const [selectedLanguageModel, setSelectedLanguageModel] = useState(''); // local ui var
  const [autoGeneration, setAutoGeneration] = useState(true); // local ui var

  // Get current settings from global state when popup opens
  useEffect(() => {
    if (isOpen) {
      const currentSettings = globalChatStateData.userChatSetting;
      setSelectedLanguageModel(currentSettings.languageModel);
      setAutoGeneration(currentSettings.imageGenMode === ImageGenMode.AUTO);
    }
  }, [isOpen]); // Only depend on isOpen, not on globalChatStateData.userChatSetting

  // Update local state when settings change (don't update global state yet)
  const updateLanguageModel = (modelId: string) => {
    setSelectedLanguageModel(modelId);
  };

  const updateImageGenMode = (isAuto: boolean) => {
    setAutoGeneration(isAuto);
  };

  // Apply changes to global state when Save is clicked
  const handleSaveSettings = async () => {
    // Update global state
    globalActions.setLanguageModel(selectedLanguageModel);
    globalActions.setImageGenMode(autoGeneration ? ImageGenMode.AUTO : ImageGenMode.OFF);
    onClose();
  };

  // Prevent body scroll when popup is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen || typeof window === 'undefined') return null;

  return createPortal(
    <PopupTemplate
      isOpen={isOpen}
      onClose={onClose}
      closeable={true}
      isTransparent={false}
      className="max-h-[65vh] flex flex-col overflow-hidden"
    >

      {/* Content */}
       <div className="flex-1 min-h-0 overflow-y-auto dark-scrollbar pt-4 px-8 pb-4 space-y-4">
        {/* Language Model Section */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-['Montserrat',Helvetica] text-lg font-medium">{t('textGeneration')}</h3>
          </div>
          <div className="space-y-3">
            {Object.values(languageModels).map((model) => (
              <div
                key={model.id}
                onClick={() => updateLanguageModel(model.id)}
                className={`w-full py-4 px-4 rounded-xl font-medium text-sm transition-all duration-200 font-['Montserrat',Helvetica] cursor-pointer ${
                  selectedLanguageModel === model.id
                    ? "bg-[#3a3a3e] text-white border-2 border-[#5856d6]"
                    : "bg-[#2a2a2e] text-gray-300 hover:bg-[#3a3a3e] border-2 border-transparent"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="text-white">
                      {model.name} <span className="text-gray-400"></span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Price with gem icon or Free */}
                    {model.price > 0 ? (
                      <div className="flex items-center gap-1">
                        <Image
                          src="/credit_icons/gem.png"
                          alt="Gem"
                          width={16}
                          height={16}
                          className="w-4 h-4"
                        />
                        <span className="text-[#b358fc] font-medium text-sm">{model.price}</span>
                      </div>
                    ) : (
                      <div className="text-sm font-medium text-gray-400">
                        {t('free')}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Image Model Section */}
        {/* <div>
          <h3 className="text-white font-['Montserrat',Helvetica] mb-4">Image Generation</h3>
          <div className="space-y-3">
            <div className="space-y-3">
              <div 
                className="py-2.5 px-4 rounded-xl font-medium text-sm transition-all duration-200 font-['Montserrat',Helvetica] cursor-pointer bg-[#2a2a2e] border-2 border-transparent hover:bg-[#3a3a3e] select-none"
                onClick={() => updateImageGenMode(!autoGeneration)}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex-1">
                    <div className="text-white">
                      Image
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1">
                      <Image
                        src="/credit_icons/gem.png"
                        alt="Gem"
                        width={16}
                        height={16}
                        className="w-4 h-4"
                      />
                      <span className="text-[#b358fc] font-medium text-sm">{IMAGE_GEN_PRICE}</span>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className={`text-sm font-['Montserrat',Helvetica] ${
                    autoGeneration ? 'text-white' : 'text-gray-400'
                  }`}>
                    {autoGeneration ? 'Auto' : 'Manual'}
                  </span>
                  <div className={`w-11 h-6 rounded-full flex items-center transition-all duration-200 ${
                    autoGeneration 
                      ? 'bg-[#5856d6] justify-end' 
                      : 'bg-gray-600 justify-start'
                  } px-1`}>
                    <div className="w-4 h-4 bg-white rounded-full"></div>
                  </div>
                </div>
              </div>
              
              <div className="text-gray-400 text-xs space-y-1 px-4">
                <div>* Image generation might have limitations. You will not be billed if the image generation is not successful.</div>
              </div>
            </div>
          </div>
        </div> */}
      </div>


      {/* Footer */}
      <div className="flex gap-3 border-t border-white/10 flex-shrink-0 p-4">
        <Button
          variant="ghost"
          className="flex-1 text-white hover:bg-[#2a2a2e] rounded-xl"
          onClick={onClose}
        >
          {t('cancel')}
        </Button>
        <Button
          className="flex-1 bg-[#5856d6] hover:bg-[#5856d6] text-white rounded-xl"
          onClick={handleSaveSettings}
        >
          {t('chooseModel')}
        </Button>
      </div>

    </PopupTemplate>,
    document.body
  );
} 